import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { DraftSyncPanel } from "@/pages/email/PilotReviewTab";
import { PilotEditorialEditor } from "@/components/email/PilotEditorialEditor";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Copy, History, ListOrdered, Lock, Radio, Workflow } from "lucide-react";
import {
  deleteStep,
  duplicateCampaign,
  duplicateStep,
  errorMessage,
  reorderSteps,
  type CampaignStep,
} from "@/api/email";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { StepsView, type StepBoardProps } from "@/components/email/StepBoard";
import { StepCanvas } from "@/components/email/StepCanvas";
import { StepEditorSheet } from "@/components/email/StepEditorSheet";
import { StepImpactDialog } from "@/components/email/StepImpactDialog";
import { PREVIOUS_TEAM_NOTE } from "@/lib/email";
import { campaignPath } from "@/lib/emailRoutes";
import { cn } from "@/lib/utils";
import { useWorkspace } from "@/pages/email/CampaignWorkspace";

type View = "steps" | "canvas";

/** The campaign's sequence, in Steps or Canvas view — the editor as it was,
 *  with its rules: once Lemlist holds leads, the sequence only grows at the
 *  end and content changes reach no one retroactively. */
export default function SequenceTab() {
  const { campaign, setCampaign, reload, meta, access } = useWorkspace();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [editor, setEditor] = useState<{ open: boolean; step: CampaignStep | null; position: number }>({
    open: false,
    step: null,
    position: 1,
  });
  // Where a step is about to be added to an active sequence: its impact shows first.
  const [pilotKey, setPilotKey] = useState<string | null>(null);
  const [pilotDirty, setPilotDirty] = useState(false);
  const [impactAt, setImpactAt] = useState<number | null>(null);
  // "kanban" is the Canvas's former place: old links still land on it.
  const view: View = ["canvas", "kanban"].includes(params.get("view") ?? "") ? "canvas" : "steps";
  const canEdit =
    access.can_manage && campaign.status !== "ARCHIVED" && !campaign.provider_legacy && !campaign.execution_lock;

  async function duplicateWhole() {
    try {
      const copy = await duplicateCampaign(campaign.project_slug, campaign.id);
      toast.success(`“${copy.name}” created as a draft`);
      navigate(campaignPath(copy.project_slug, copy.id));
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }

  const boardProps: StepBoardProps = {
    campaign,
    canEdit,
    onEdit: (step) => setEditor({ open: true, step, position: step.position }),
    onAdd: (position) => {
      const at = position ?? campaign.steps.length + 1;
      if (campaign.sequence.active_sequence) setImpactAt(at);
      else setEditor({ open: true, step: null, position: at });
    },
    onDuplicate: async (step) => {
      if (campaign.sequence.active_sequence) {
        const ok = await confirm({
          title: `Duplicate “${step.name}” to the end?`,
          description:
            "This campaign is active: the copy goes after the last step. At the next sync, leads who " +
            "haven't passed it get their own email for it; nobody gets it retroactively.",
          confirmLabel: "Duplicate",
        });
        if (!ok) return;
      }
      try {
        setCampaign(await duplicateStep(campaign.project_slug, step.id));
        toast.success(`“${step.name}” duplicated`);
      } catch (err) {
        toast.error(errorMessage(err));
      }
    },
    onDelete: async (step) => {
      const active = campaign.sequence.active_sequence;
      const ok = await confirm({
        title: `Delete “${step.name}”?`,
        description: !active
          ? "Its template and AI variables are deleted. The steps after it move up."
          : step.capabilities?.pending
            ? "It is not in Lemlist yet: it is removed from Prism only."
            : "Lemlist keeps every step once leads are in the campaign: Prism parks this one there, out of " +
              "everyone's reach, and reuses it for the next follow-up you add. Leads who got the step before " +
              "it have completed the sequence.",
        confirmLabel: "Delete step",
        variant: "danger",
      });
      if (!ok) return;
      try {
        setCampaign(await deleteStep(campaign.project_slug, step.id));
        toast.success("Step deleted");
      } catch (err) {
        toast.error(errorMessage(err));
      }
    },
    onReorder: async (stepIds) => {
      const previous = campaign;
      // Optimistic: the new order shows at once, positions renumbered.
      setCampaign({
        ...campaign,
        steps: stepIds.map((sid, index) => ({ ...campaign.steps.find((s) => s.id === sid)!, position: index + 1 })),
      });
      try {
        setCampaign(await reorderSteps(campaign.project_slug, campaign.id, stepIds));
      } catch (err) {
        setCampaign(previous);
        toast.error(errorMessage(err));
      }
    },
  };

  if (campaign.execution_lock && campaign.pilot?.editorial) return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-500">{campaign.pilot.cadence.steps.map((s) => s.day ? `J+${s.day}` : "J0").join(" → ")} · Cliquez sur un email</p>
        <DraftSyncPanel compact />
      </div>
      <StepCanvas {...boardProps} onEdit={(step) => setPilotKey(step.position === 1 ? "1:A" : String(step.position))}
        onPilotVariant={(variant) => setPilotKey(`1:${variant}`)} />
      <Sheet open={pilotKey !== null} onOpenChange={async (open) => {
        if (!open && pilotDirty && !await confirm({ title: "Quitter sans enregistrer ?", description: "Vos modifications ne seront pas conservées.", confirmLabel: "Quitter" })) return;
        if (!open) { setPilotKey(null); setPilotDirty(false); }
      }}>
        <SheetContent className="flex w-full flex-col p-0 sm:max-w-[740px]" aria-describedby={undefined}>
          <SheetTitle className="sr-only">Email de la séquence</SheetTitle>
          {pilotKey && <PilotEditorialEditor key={pilotKey} initialKey={pilotKey} onDirtyChange={setPilotDirty} />}
        </SheetContent>
      </Sheet>
    </div>
  );

  return (
    <div className="space-y-4">
      {campaign.execution_lock && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          <Lock className="h-4 w-4 shrink-0 text-amber-700" />
          <p className="min-w-0 flex-1">
            <span className="mr-1.5 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
              Locked pilot
            </span>
            These steps hold the commercial document&apos;s templates. Their {"{{…}}"} fields are filled prospect by
            prospect: read each draft in Review. Prism cannot send these templates, and nothing here can be edited.
          </p>
        </div>
      )}
      {campaign.provider_legacy && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
          <History className="h-4 w-4 shrink-0 text-slate-500" />
          <p className="min-w-0 flex-1">
            <span className="mr-1.5 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-700">
              Previous Lemlist team
            </span>
            {PREVIOUS_TEAM_NOTE}
          </p>
          {access.can_manage && (
            <Button size="sm" variant="outline" className="bg-white" onClick={() => void duplicateWhole()}>
              <Copy className="h-3.5 w-3.5" /> Duplicate
            </Button>
          )}
        </div>
      )}

      {campaign.sequence.active_sequence && campaign.status !== "ARCHIVED" && !campaign.provider_legacy && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-emerald-100 bg-emerald-50/70 px-4 py-2.5 text-sm text-emerald-950">
          <Radio className="h-4 w-4 shrink-0 text-emerald-600" />
          <p className="min-w-0 flex-1">
            <span className="mr-1.5 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800">
              Active sequence
            </span>
            {campaign.sequence.message} To reorder or insert steps, duplicate the campaign.
          </p>
          {access.can_manage && (
            <Button size="sm" variant="outline" className="bg-white" onClick={() => void duplicateWhole()}>
              <Copy className="h-3.5 w-3.5" /> Duplicate
            </Button>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div
          role="radiogroup"
          aria-label="View"
          className="inline-flex rounded-lg bg-white p-0.5 shadow-sm-tile ring-1 ring-slate-900/[0.04]"
        >
          {(
            [
              { value: "steps", label: "Steps", icon: ListOrdered },
              { value: "canvas", label: "Canvas", icon: Workflow },
            ] as const
          ).map((option) => (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={view === option.value}
              onClick={() => {
                const next = new URLSearchParams(params);
                if (option.value === "steps") next.delete("view");
                else next.set("view", option.value);
                setParams(next, { replace: true });
              }}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                view === option.value ? "bg-indigo-600 text-white shadow-cta" : "text-slate-500 hover:text-slate-800",
              )}
            >
              <option.icon className="h-3.5 w-3.5" />
              {option.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-400">
          {view === "steps" && canEdit && campaign.sequence.can_reorder_steps && campaign.steps.length > 1
            ? "Drag a step to reorder the sequence."
            : null}
        </p>
      </div>

      {view === "steps" ? <StepsView {...boardProps} /> : <StepCanvas {...boardProps} />}

      <StepEditorSheet
        open={editor.open}
        onOpenChange={(open) => setEditor((e) => ({ ...e, open }))}
        campaign={campaign}
        step={editor.step}
        newPosition={editor.position}
        variables={meta.variables}
        canEdit={canEdit}
        onSaved={(detail) => (detail ? setCampaign(detail) : void reload())}
      />
      <StepImpactDialog
        campaign={campaign}
        position={impactAt}
        onOpenChange={(open) => !open && setImpactAt(null)}
        onContinue={(at) => {
          setImpactAt(null);
          setEditor({ open: true, step: null, position: at });
        }}
      />
    </div>
  );
}
